<template>
  <div class="flex min-h-screen w-full flex-col lg:flex-row bg-background-light">

    <ClientSidebar />

    <!-- Hidden file input -->
    <input ref="fileInput" type="file" class="hidden" @change="handleFileUpload" />

    <main class="flex-1 flex flex-col min-h-screen relative">

      <!-- Loading -->
      <div v-if="loading" class="flex flex-col items-center justify-center flex-1 gap-4 p-12">
        <svg class="animate-spin h-10 w-10 text-secondary" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="text-slate-400 text-sm">Loading your dashboard...</p>
      </div>

      <div v-else class="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto w-full space-y-8">

        <!-- ── Welcome Header ── -->
        <section class="flex flex-col md:flex-row gap-6 justify-between items-start md:items-end">
          <div class="flex flex-col gap-2">
            <h1 class="text-3xl md:text-4xl font-extrabold text-secondary tracking-tight">
              Welcome back, {{ firstName }}
            </h1>
            <p class="text-slate-500 font-medium">
              Your {{ clientRecord.taxYear || new Date().getFullYear() }} {{ clientRecord.serviceType || 'Tax Return' }} is currently {{ (clientRecord.status || 'in progress').toLowerCase() }}.
            </p>
          </div>
          <div v-if="hasActionRequired"
            class="flex items-center gap-3 bg-white px-4 py-2 rounded-full shadow-sm border border-slate-100">
            <span class="relative flex h-3 w-3">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span class="relative inline-flex rounded-full h-3 w-3 bg-primary"></span>
            </span>
            <span class="text-sm font-bold text-secondary uppercase tracking-wider">Action Required</span>
          </div>
        </section>

        <!-- ── Stats Row ── -->
        <section>
          <!-- Return Status -->
          <div class="bg-secondary rounded-xl p-6 text-white relative overflow-hidden group">
            <div class="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">

              <!-- Left: status + donut chart -->
              <div class="flex items-center gap-6 flex-1">
                <!-- Donut Chart -->
                <div class="relative flex-shrink-0 w-24 h-24">
                  <svg viewBox="0 0 36 36" class="w-24 h-24 -rotate-90">
                    <!-- Background ring -->
                    <circle cx="18" cy="18" r="15.9" fill="none"
                      stroke="rgba(255,255,255,0.1)" stroke-width="3.5" />
                    <!-- Progress arc -->
                    <circle cx="18" cy="18" r="15.9" fill="none"
                      stroke="#F5B800"
                      stroke-width="3.5"
                      stroke-linecap="round"
                      :stroke-dasharray="`${completionPercent} ${100 - completionPercent}`"
                      stroke-dashoffset="0"
                      style="transition: stroke-dasharray 0.7s ease" />
                  </svg>
                  <!-- Centre label -->
                  <div class="absolute inset-0 flex flex-col items-center justify-center">
                    <span class="text-xl font-extrabold text-primary leading-none">{{ completionPercent }}%</span>
                    <span class="text-[9px] text-slate-400 font-medium mt-0.5">complete</span>
                  </div>
                </div>
                <!-- Status text -->
                <div class="flex flex-col gap-1">
                  <p class="text-slate-300 text-sm font-medium">Return Status</p>
                  <h3 class="text-2xl font-bold tracking-tight">{{ clientRecord.status || 'In Progress' }}</h3>
                  <p class="text-xs text-slate-400 mt-1">
                    {{ activeTaskGroup ? activeTaskGroup.doneCount + ' of ' + activeTaskGroup.tasks.length + ' tasks completed' : 'No tasks yet' }}
                  </p>
                </div>
              </div>

              <!-- Right: next task due date -->
              <div v-if="nextDueTask" class="flex-shrink-0 bg-white/10 rounded-xl p-4 flex flex-col gap-1 min-w-[160px]">
                <p class="text-xs text-slate-300 font-medium">Next Task Due</p>
                <p class="text-lg font-bold" :class="nextDueTask.daysRemaining === 'Overdue' ? 'text-red-400' : 'text-primary'">
                  {{ nextDueTask.daysRemaining }}
                </p>
                <p class="text-xs text-slate-300 truncate max-w-[160px]">{{ nextDueTask.Title }}</p>
              </div>
              <div v-else class="flex-shrink-0 bg-white/10 rounded-xl p-4 flex flex-col gap-1 min-w-[160px]">
                <p class="text-xs text-slate-300 font-medium">Next Task Due</p>
                <p class="text-base font-bold text-slate-400">No deadline set</p>
              </div>

            </div>
          </div>
        </section>

        <!-- ── Main Content ── -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">

          <!-- LEFT: Tasks + Files -->
          <div class="lg:col-span-2 flex flex-col gap-8">

            <!-- My Tasks -->
            <div class="flex flex-col gap-4">
              <div class="flex items-center justify-between">
                <h2 class="text-xl font-bold text-secondary flex items-center gap-2">
                  <span class="material-symbols-outlined text-primary">check_circle</span>
                  My Tasks
                </h2>
                <span v-if="pendingTaskCount > 0"
                  class="bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs font-bold">
                  {{ pendingTaskCount }} Pending
                </span>
              </div>

              <!-- No tasks at all -->
              <div v-if="tasks.length === 0"
                class="bg-white rounded-xl border border-slate-200 shadow-sm p-10 flex flex-col items-center text-center">
                <span class="material-symbols-outlined text-slate-300 text-[40px]">task_alt</span>
                <p class="text-slate-400 text-sm mt-2">No tasks assigned yet.</p>
              </div>

              <div v-else class="flex flex-col gap-4">

                <!-- ── Template Tabs ── -->
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="group in taskGroups"
                    :key="group.templateId"
                    @click="activeTemplateTab = group.templateId"
                    class="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all"
                    :class="activeTemplateTab === group.templateId
                      ? 'bg-secondary text-white border-secondary shadow-sm'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-secondary hover:text-secondary'">
                    <span>{{ group.name }}</span>
                    <span
                      class="px-1.5 py-0.5 rounded-full text-xs font-bold"
                      :class="activeTemplateTab === group.templateId
                        ? 'bg-white/20 text-white'
                        : 'bg-slate-100 text-slate-500'">
                      {{ group.doneCount }}/{{ group.tasks.length }}
                    </span>
                  </button>
                </div>

                <!-- ── Tasks for selected template ── -->
                <div v-if="activeTaskGroup" class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden divide-y divide-slate-100">

                  <!-- Progress bar for this template -->
                  <div class="px-4 py-3 bg-slate-50 flex items-center gap-3">
                    <div class="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div class="h-full bg-primary rounded-full transition-all duration-700"
                        :style="{ width: activeTaskGroup.progressPercent + '%' }"></div>
                    </div>
                    <span class="text-xs font-bold text-slate-500 flex-shrink-0">
                      {{ activeTaskGroup.progressPercent }}% complete
                    </span>
                  </div>

                  <div
                    v-for="task in activeTaskGroup.tasks"
                    :key="task.Id"
                    class="p-4 flex flex-col sm:flex-row sm:items-center gap-4 transition-opacity"
                    :class="getStatus(task) === 'Completed'
                      ? 'bg-slate-50/50 opacity-60 hover:opacity-100'
                      : task.Priority === 'Urgent'
                        ? 'hover:bg-slate-50 border-l-4 border-l-red-500'
                        : 'hover:bg-slate-50 border-l-4 border-l-primary'">

                    <div v-if="getStatus(task) === 'Completed'"
                      class="p-1 rounded-full bg-green-100 text-green-600 flex-shrink-0">
                      <span class="material-symbols-outlined text-[20px]">check</span>
                    </div>

                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 v-if="getStatus(task) !== 'Completed'"
                          class="font-bold text-secondary text-base">
                          {{ task.Title }}
                        </h3>
                        <h3 v-else
                          class="font-bold text-slate-700 text-sm line-through decoration-slate-400">
                          {{ task.Title }}
                        </h3>
                        <span
                          v-if="getStatus(task) !== 'Completed' && (task.Priority === 'Urgent' || task.Priority === 'High')"
                          class="px-2 py-0.5 rounded text-[10px] font-bold uppercase"
                          :class="task.Priority === 'Urgent' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'">
                          {{ task.Priority === 'Urgent' ? 'Urgent' : 'Action' }}
                        </span>
                      </div>
                      <p v-if="getStatus(task) !== 'Completed'" class="text-sm text-slate-500">
                        {{ task.Description }}
                      </p>
                      <p v-else class="text-xs text-slate-400">
                        Completed{{ task.Modified ? ' on ' + formatShortDate(task.Modified) : '' }}
                      </p>
                      <div v-if="uploadingTaskId === task.Id" class="mt-2">
                        <div class="flex items-center gap-2">
                          <div class="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div class="h-full bg-primary rounded-full transition-all duration-300"
                              :style="{ width: uploadProgress + '%' }"></div>
                          </div>
                          <span class="text-xs text-slate-400">{{ uploadProgress }}%</span>
                        </div>
                        <p class="text-xs text-slate-400 mt-1">Uploading {{ uploadingFileName }}...</p>
                      </div>
                    </div>

                    <div class="flex items-center gap-3 flex-shrink-0">
                      <span v-if="task.DueDate && getStatus(task) !== 'Completed'"
                        class="text-xs font-medium text-slate-400">
                        Due: {{ formatShortDate(task.DueDate) }}
                      </span>
                      <span v-if="getStatus(task) === 'Completed'"
                        class="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded">
                        Done
                      </span>
                      <div v-else-if="uploadingTaskId === task.Id"
                        class="px-4 py-2 rounded-lg bg-slate-100 text-slate-400 text-sm font-bold flex items-center gap-2">
                        <svg class="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Uploading...
                      </div>
                      <router-link
                        v-else-if="task.Category === 'Signature' || task.Category === 'Review'"
                        to="/client/documents"
                        class="bg-secondary text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-secondary-light transition-colors shadow-sm">
                        Review & Sign
                      </router-link>
                      <button
                        v-else
                        @click="triggerUpload(task)"
                        class="border border-slate-200 text-secondary px-4 py-2 rounded-lg text-sm font-bold hover:border-primary hover:text-primary transition-colors bg-white">
                        Upload
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <!-- Recent Files -->
            <div class="flex flex-col gap-4">
              <div class="flex items-center justify-between">
                <h2 class="text-xl font-bold text-secondary flex items-center gap-2">
                  <span class="material-symbols-outlined text-primary">folder_open</span>
                  Recent Files
                </h2>
                <router-link to="/client/documents"
                  class="text-sm text-primary font-bold hover:underline">
                  View All →
                </router-link>
              </div>

              <div v-if="recentFiles.length === 0"
                class="bg-white rounded-xl border border-slate-200 shadow-sm p-10 flex flex-col items-center text-center">
                <span class="material-symbols-outlined text-slate-300 text-[40px]">folder_open</span>
                <p class="text-slate-400 text-sm mt-2">No files uploaded yet.</p>
                <router-link to="/client/documents" class="mt-2 text-xs text-primary hover:underline">
                  Upload your first document
                </router-link>
              </div>

              <div v-else class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div
                  v-for="file in recentFiles"
                  :key="file.FileUrl"
                  class="flex items-center gap-4 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors group">
                  <!-- Icon -->
                  <div class="p-2 rounded-lg flex-shrink-0" :class="getFileBg(file.Name)">
                    <span class="material-symbols-outlined text-[20px]" :class="getFileIconColor(file.Name)">
                      {{ getFileIcon(file.Name) }}
                    </span>
                  </div>
                  <!-- Name + meta -->
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-semibold text-secondary truncate" :title="file.Name">{{ file.Name }}</p>
                    <p class="text-xs text-slate-400 mt-0.5">
                      {{ file.Category === 'Uploaded Documents' ? 'Uploaded by You' : 'Added by Admin' }}
                      · {{ formatFileDate(file.Created) }}
                    </p>
                  </div>
                  <!-- Download -->
                  <a
                    :href="buildFileUrl(file)"
                    target="_blank"
                    class="flex-shrink-0 flex items-center gap-1 text-xs font-bold text-slate-400 hover:text-primary transition-colors px-2 py-1.5 rounded hover:bg-primary/10">
                    <span class="material-symbols-outlined text-[16px]">download</span>
                    Download
                  </a>
                </div>
              </div>
            </div>

          </div>

          <!-- RIGHT: Announcements -->
          <div class="lg:col-span-1 flex flex-col gap-4">
            <h2 class="text-xl font-bold text-secondary flex items-center gap-2">
              <span class="material-symbols-outlined text-primary">campaign</span>
              Announcements
            </h2>

            <!-- Empty -->
            <div v-if="announcements.length === 0"
              class="bg-white rounded-xl border border-slate-200 shadow-sm p-8 flex flex-col items-center text-center">
              <span class="material-symbols-outlined text-slate-300 text-[36px]">campaign</span>
              <p class="text-slate-400 text-sm mt-2">No announcements yet.</p>
            </div>

            <!-- Single card — collapsible feed -->
            <div v-else class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div
                v-for="ann in announcements"
                :key="ann.Id"
                class="border-b border-slate-100 last:border-0">

                <!-- Header row — click to expand -->
                <button
                  @click="expandedAnnId = expandedAnnId === ann.Id ? null : ann.Id"
                  class="w-full flex items-start justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left">
                  <div class="flex flex-col gap-0.5 min-w-0 flex-1">
                    <span class="text-xs font-bold uppercase text-primary">
                      {{ ann.RecipientDisplay || 'Firm News' }}
                    </span>
                    <span class="text-sm font-semibold text-secondary">{{ ann.Subject }}</span>
                  </div>
                  <div class="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                    <span class="text-xs text-slate-400">{{ formatRelativeDate(ann.Created) }}</span>
                    <span v-if="ann.AttachmentUrl" class="material-symbols-outlined text-primary" style="font-size: 14px;">attach_file</span>
                    <span class="material-symbols-outlined text-slate-400 transition-transform"
                      :class="expandedAnnId === ann.Id ? 'rotate-180' : ''"
                      style="font-size: 18px;">expand_more</span>
                  </div>
                </button>

                <!-- Expanded body -->
                <div v-if="expandedAnnId === ann.Id" class="px-4 pb-4 bg-slate-50/50">
                  <p class="text-xs text-slate-500 leading-relaxed whitespace-pre-line mb-3">
                    {{ ann.DisplayMessage }}
                  </p>
                  <a v-if="ann.AttachmentUrl || (ann.DriveItemId && ann.DriveId)"
                    :href="buildAttachmentUrl(ann)"
                    target="_blank"
                    class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors">
                    <span class="material-symbols-outlined" style="font-size: 14px;">download</span>
                    {{ ann.AttachmentName || 'Download Attachment' }}
                  </a>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
      <!-- Disclaimer -->
      <p class="px-4 md:px-8 lg:px-12 py-4 text-center leading-relaxed mt-auto" style="font-size:9px; font-style:italic; color:rgba(0,0,0,0.8);">
        <span class="font-semibold not-italic">Disclaimer:</span> By uploading documents to this portal, you acknowledge that while we employ industry-standard security to protect your data, the transmission of information over the internet is not entirely risk-free. ABC TAX ADVISORY utilizes this third-party platform for your convenience but assumes no responsibility for data security or technical issues arising from its use.
      </p>
    </main>
  </div>
</template>

<script>
import ClientSidebar from '../../components/ClientSidebar.vue';

export default {
  name: 'ClientDashboard',
  components: { ClientSidebar },

  data() {
    return {
      loading: true,
      tasks: [],
      announcements: [],
      recentFiles: [],
      uploadingTaskId: null,
      uploadingFileName: '',
      uploadProgress: 0,
      selectedTask: null,
      activeTemplateTab: null,
      expandedAnnId: null,
      templateNames: {}
    };
  },

  computed: {
    clientRecord() {
      try {
        const token = sessionStorage.getItem('clientSession');
        if (!token) return {};
        const [data] = token.split('.');
        if (!data) return {};
        const payload = JSON.parse(atob(data.replace(/-/g, '+').replace(/_/g, '/')));
        if (!payload.exp || Date.now() > payload.exp) return {};
        return payload;
      } catch { return {}; }
    },

    firstName() {
      const name = this.clientRecord?.name || 'Client';
      return name.split(' ')[0];
    },


    completionPercent() {
      const group = this.activeTaskGroup;
      if (!group || !group.tasks.length) return 0;
      return group.progressPercent;
    },

    pendingTaskCount() {
      return this.tasks.filter(t => this.getStatus(t) !== 'Completed').length;
    },

    nextDueTask() {
      // Earliest pending task from the currently active template tab
      const group = this.activeTaskGroup;
      if (!group) return null;
      const pending = group.tasks.filter(t =>
        this.getStatus(t) !== 'Completed' && t.DueDate
      );
      if (!pending.length) return null;
      const task = pending.sort((a, b) => new Date(a.DueDate) - new Date(b.DueDate))[0];
      const days = Math.ceil((new Date(task.DueDate) - new Date()) / 86400000);
      return {
        ...task,
        daysRemaining: days < 0 ? 'Overdue' : days === 0 ? 'Today' : `${days} Days`
      };
    },

    taskGroups() {
      // Group tasks by TemplateId
      const groups = {};
      this.tasks.forEach(task => {
        const tid = task.TemplateId || 0;
        if (!groups[tid]) groups[tid] = [];
        groups[tid].push(task);
      });

      return Object.entries(groups).map(([tid, tasks]) => {
        const templateId = parseInt(tid) || 0;
        const name = templateId
          ? (this.templateNames[templateId] || `Template ${templateId}`)
          : 'General';
        const doneCount = tasks.filter(t => this.getStatus(t) === 'Completed').length;
        const progressPercent = tasks.length ? Math.round((doneCount / tasks.length) * 100) : 0;
        return { templateId, name, tasks, doneCount, progressPercent };
      }).sort((a, b) => {
        // General always last
        if (a.templateId === 0) return 1;
        if (b.templateId === 0) return -1;
        return a.name.localeCompare(b.name);
      });
    },

    activeTaskGroup() {
      return this.taskGroups.find(g => g.templateId === this.activeTemplateTab) || this.taskGroups[0] || null;
    },

    hasActionRequired() {
      return this.tasks.some(t =>
        this.getStatus(t) !== 'Completed' &&
        (t.Priority === 'Urgent' || t.Priority === 'High')
      );
    },

  },

  async mounted() {
    await this.loadDashboard();
  },

  methods: {

    getStatus(task) {
      return task.TaskStatus || task.Status || '';
    },

    async loadDashboard() {
      this.loading = true;
      try {
        const clientName = this.clientRecord?.name;
        if (!clientName) return;

        const [tasks, announcements, files, templates] = await Promise.all([
          this.$sharepoint.getClientTasksByName(clientName),
          this.$sharepoint.getClientAnnouncements(clientName),
          this.$sharepoint.getClientFiles(clientName),
          this.$sharepoint.getTemplates().catch(() => [])
        ]);

        // Build templateId → name map
        const nameMap = {};
        (templates || []).forEach(t => { nameMap[t.id] = t.name; });
        this.templateNames = nameMap;

        this.tasks = this.sortTasks(tasks);
        this.announcements = announcements.slice(0, 3);
        this.recentFiles = files
          .sort((a, b) => new Date(b.Created) - new Date(a.Created))
          .slice(0, 4);

        // Auto-select first template tab
        if (this.taskGroups.length > 0) {
          this.activeTemplateTab = this.taskGroups[0].templateId;
        }

      } catch (error) {
        console.error('Error loading dashboard:', error);
      } finally {
        this.loading = false;
      }
    },

    sortTasks(tasks) {
      return [...tasks].sort((a, b) => {
        const aComp = this.getStatus(a) === 'Completed';
        const bComp = this.getStatus(b) === 'Completed';
        if (aComp && !bComp) return 1;
        if (!aComp && bComp) return -1;
        return 0;
      });
    },

    // ── Upload ──────────────────────────────────────────────────
    triggerUpload(task) {
      this.selectedTask = task;
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file || !this.selectedTask) return;

      const task = this.selectedTask;
      this.uploadingTaskId = task.Id;
      this.uploadingFileName = file.name;
      this.uploadProgress = 0;

      try {
        const clientName = this.clientRecord?.name;
        if (!clientName) throw new Error('No client name found');

        const progressInterval = setInterval(() => {
          if (this.uploadProgress < 85) this.uploadProgress += 10;
        }, 200);

        const uploaded = await this.$sharepoint.uploadDocument(file, {
          clientName: clientName,
          category: 'Uploaded Documents'
        });

        clearInterval(progressInterval);
        this.uploadProgress = 100;
        await new Promise(r => setTimeout(r, 400));

        // ✅ Persist Completed status to SharePoint so admin & client see it after refresh
        try {
          await this.$sharepoint.updateTaskStatus(task.Id, 'Completed');
        } catch (statusError) {
          console.error('Could not update task status in SharePoint:', statusError);
          // Upload succeeded — still mark completed locally even if status call fails
        }

        // ✅ Clear upload state
        this.uploadingTaskId = null;
        this.uploadProgress = 0;

        // ✅ Permanently flip task to Completed locally
        const taskIndex = this.tasks.findIndex(t => t.Id === task.Id);
        if (taskIndex !== -1) {
          const updated = { ...this.tasks[taskIndex] };
          updated.TaskStatus = 'Completed';
          updated.Modified = new Date().toISOString();
          this.tasks.splice(taskIndex, 1, updated);
        }

        // ✅ Re-sort so completed task slides to bottom
        this.tasks = this.sortTasks(this.tasks);

        // ✅ Prepend to recent files
        this.recentFiles.unshift(uploaded);
        this.recentFiles = this.recentFiles.slice(0, 4);

      } catch (error) {
        console.error('Upload error:', error);
        this.uploadingTaskId = null;
        this.uploadProgress = 0;
        alert('Upload failed. Please try again or go to Documents.');
      }
    },

    // ── File Helpers ────────────────────────────────────────────
    getFileType(filename) {
      const ext = (filename || '').split('.').pop()?.toLowerCase();
      if (ext === 'pdf') return 'pdf';
      if (['docx', 'doc'].includes(ext)) return 'word';
      if (['xlsx', 'xls'].includes(ext)) return 'excel';
      if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'image';
      return 'file';
    },

    getFileIcon(filename) {
      const map = { pdf: 'picture_as_pdf', word: 'description', excel: 'table_view', image: 'image', file: 'insert_drive_file' };
      return map[this.getFileType(filename)] || 'insert_drive_file';
    },

    getFileBg(filename) {
      const map = { pdf: 'bg-red-50', word: 'bg-blue-50', excel: 'bg-green-50', image: 'bg-blue-50', file: 'bg-slate-50' };
      return map[this.getFileType(filename)] || 'bg-slate-50';
    },

    getFileIconColor(filename) {
      const map = { pdf: 'text-red-500', word: 'text-blue-500', excel: 'text-green-500', image: 'text-blue-500', file: 'text-slate-400' };
      return map[this.getFileType(filename)] || 'text-slate-400';
    },

    formatShortDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    },

    formatRelativeDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffDays = Math.floor((now - date) / 86400000);
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },

    buildFileUrl(file) {
      const token = sessionStorage.getItem('clientSession') || '';
      if (file.DriveItemId && file.DriveId) {
        return `/.netlify/functions/get-file?driveId=${encodeURIComponent(file.DriveId)}&itemId=${encodeURIComponent(file.DriveItemId)}&token=${encodeURIComponent(token)}`;
      }
      return file.FileUrl || '#';
    },

    buildAttachmentUrl(ann) {
      const token = sessionStorage.getItem('clientSession') || '';
      if (ann.DriveItemId && ann.DriveId) {
        return `/.netlify/functions/get-file?driveId=${encodeURIComponent(ann.DriveId)}&itemId=${encodeURIComponent(ann.DriveItemId)}&token=${encodeURIComponent(token)}`;
      }
      if (ann.AttachmentUrl) {
        return `/.netlify/functions/get-file?path=${encodeURIComponent(ann.AttachmentUrl)}&token=${encodeURIComponent(token)}`;
      }
      return '#';
    },

    formatFileDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true
      });
    }
  }
};
</script>